import React, { useState, useEffect } from "react";
import { Calendar, Clock, User, ArrowRight, Search, Tag } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedPost, setSelectedPost] = useState(null);

  const categories = ["All", "ICBC", "HeadWorks", "Prevention", "Recovery", "Wellness"];

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/blog`);
        setPosts(response.data);
        setFilteredPosts(response.data);
      } catch (error) {
        console.error("Error fetching blog posts:", error);
      }
    };
    fetchPosts();
  }, []);

  useEffect(() => {
    let filtered = posts;
    
    if (selectedCategory !== "All") {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(post => 
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredPosts(filtered);
  }, [searchTerm, selectedCategory, posts]);

  if (selectedPost) {
    return (
      <BlogPostDetail 
        post={selectedPost} 
        onBack={() => setSelectedPost(null)} 
        relatedPosts={posts.filter(p => p.id !== selectedPost.id).slice(0, 3)}
        onSelectPost={setSelectedPost}
      />
    );
  }

  return (
    <section id="blog" className="py-20 md:py-28 bg-white" data-testid="blog-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-[#0F4C3A] font-semibold text-sm uppercase tracking-wider">
            Health Resources
          </span>
          <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
            Expert <span className="text-[#0F4C3A]">Health Insights</span>
          </h2>
          <p className="mt-6 text-lg text-gray-600">
            Stay informed with the latest tips, guides, and insights from our team of 
            healthcare professionals to help you on your recovery journey.
          </p>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-12">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 py-6 rounded-full border-gray-200 focus:border-[#0F4C3A]"
              data-testid="blog-search-input"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? "bg-[#0F4C3A] text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
                data-testid={`blog-category-${category.toLowerCase()}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Featured Post */}
        {filteredPosts.length > 0 && (
          <div 
            className="mb-12 cursor-pointer group"
            onClick={() => setSelectedPost(filteredPosts[0])}
            data-testid="featured-blog-post"
          >
            <div className="grid md:grid-cols-2 gap-8 bg-gradient-to-br from-[#D1FAE5]/30 to-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="h-64 md:h-auto overflow-hidden">
                <img
                  src={filteredPosts[0].image_url}
                  alt={filteredPosts[0].title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-4 mb-4">
                  <span className="bg-[#0F4C3A] text-white px-3 py-1 rounded-full text-xs font-semibold">
                    Featured
                  </span>
                  <span className="text-[#0F4C3A] text-sm font-medium">
                    {filteredPosts[0].category}
                  </span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 group-hover:text-[#0F4C3A] transition-colors">
                  {filteredPosts[0].title}
                </h3>
                <p className="text-gray-600 mb-6 line-clamp-3">
                  {filteredPosts[0].excerpt}
                </p>
                <div className="flex items-center gap-6 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span>{filteredPosts[0].author}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{filteredPosts[0].published_at}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>{filteredPosts[0].read_time} min read</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Blog Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPosts.slice(1).map((post) => (
            <article
              key={post.id}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer border border-gray-100"
              onClick={() => setSelectedPost(post)}
              data-testid={`blog-post-${post.id}`}
            >
              <div className="h-48 overflow-hidden">
                <img
                  src={post.image_url}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="bg-[#D1FAE5] text-[#064E3B] px-3 py-1 rounded-full text-xs font-semibold">
                    {post.category}
                  </span>
                  <span className="text-gray-400 text-sm">{post.read_time} min</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#0F4C3A] transition-colors line-clamp-2">
                  {post.title}
                </h3>
                <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">{post.published_at}</span>
                  <span className="text-[#0F4C3A] font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                    Read More <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>

        {filteredPosts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No articles found matching your criteria.</p>
            <button
              onClick={() => { setSearchTerm(""); setSelectedCategory("All"); }}
              className="mt-4 text-[#0F4C3A] font-semibold hover:underline"
            >
              Clear filters
            </button>
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 text-center bg-[#0F4C3A] rounded-3xl p-12">
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Start Your Recovery?
          </h3>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Don't let pain hold you back. Our team of experts is ready to help you 
            get back to doing what you love.
          </p>
          <a
            href="https://bc-ice.janeapp.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="blog-cta-book-btn"
          >
            <Button className="bg-white text-[#0F4C3A] hover:bg-gray-100 rounded-full px-8 py-4 font-semibold shadow-lg">
              Book Your Appointment
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

// Blog Post Detail Component
const BlogPostDetail = ({ post, onBack, relatedPosts, onSelectPost }) => {
  return (
    <article className="py-20 md:py-28 bg-white" data-testid="blog-post-detail">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-[#0F4C3A] font-semibold mb-8 hover:gap-3 transition-all"
          data-testid="blog-back-btn"
        >
          <ArrowRight className="w-4 h-4 rotate-180" />
          Back to Articles
        </button>

        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <span className="bg-[#D1FAE5] text-[#064E3B] px-4 py-1 rounded-full text-sm font-semibold">
              {post.category}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-6 text-gray-500">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5" />
              <span>{post.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span>{post.published_at}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span>{post.read_time} min read</span>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        <div className="rounded-3xl overflow-hidden mb-12">
          <img
            src={post.image_url}
            alt={post.title}
            className="w-full h-[400px] object-cover"
          />
        </div>

        {/* Content */}
        <div className="prose prose-lg max-w-none mb-12">
          <p className="text-xl text-gray-600 leading-relaxed mb-8">
            {post.excerpt}
          </p>
          
          {/* Full article content based on category */}
          {post.category === "ICBC" && (
            <ICBCArticleContent />
          )}
          {post.category === "HeadWorks" && (
            <TMDArticleContent />
          )}
          {post.category === "Prevention" && (
            <PreventionArticleContent />
          )}
          {!["ICBC", "HeadWorks", "Prevention"].includes(post.category) && (
            <GeneralArticleContent />
          )}
        </div>

        {/* CTA Box */}
        <div className="bg-gradient-to-br from-[#D1FAE5]/50 to-white rounded-2xl p-8 mb-12">
          <h3 className="text-xl font-bold text-gray-900 mb-3">
            Need Help With Your Condition?
          </h3>
          <p className="text-gray-600 mb-6">
            Our team of certified professionals is ready to help you find relief and get back to your daily activities.
          </p>
          <a
            href="https://bc-ice.janeapp.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button className="bg-[#0F4C3A] hover:bg-[#1A6B52] text-white rounded-full px-6 py-3 font-semibold">
              Book a Consultation
            </Button>
          </a>
        </div>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="border-t border-gray-200 pt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Related Articles</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <div
                  key={relatedPost.id}
                  className="cursor-pointer group"
                  onClick={() => onSelectPost(relatedPost)}
                >
                  <div className="h-32 rounded-xl overflow-hidden mb-3">
                    <img
                      src={relatedPost.image_url}
                      alt={relatedPost.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  </div>
                  <h4 className="font-semibold text-gray-900 group-hover:text-[#0F4C3A] transition-colors line-clamp-2">
                    {relatedPost.title}
                  </h4>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

// Article Content Components
const ICBCArticleContent = () => (
  <div className="space-y-6 text-gray-700">
    <h2 className="text-2xl font-bold text-gray-900">Understanding Your ICBC Benefits</h2>
    <p>
      If you've been injured in a motor vehicle accident in British Columbia, you may be entitled 
      to physiotherapy coverage through ICBC. Understanding your benefits can help you access 
      the care you need without financial stress.
    </p>
    
    <h3 className="text-xl font-bold text-gray-900">What's Covered?</h3>
    <ul className="list-disc pl-6 space-y-2">
      <li>Physiotherapy assessments and treatments</li>
      <li>Massage therapy sessions</li>
      <li>Kinesiology and active rehabilitation</li>
      <li>Acupuncture treatments</li>
      <li>Medical equipment when prescribed</li>
    </ul>

    <h3 className="text-xl font-bold text-gray-900">How to Access Your Benefits</h3>
    <ol className="list-decimal pl-6 space-y-2">
      <li>Report your accident to ICBC and obtain your claim number</li>
      <li>Visit your family doctor for an initial assessment</li>
      <li>Get a referral for physiotherapy if needed</li>
      <li>Book an appointment at BC-ICE - we handle direct billing</li>
      <li>Focus on your recovery while we manage the paperwork</li>
    </ol>

    <div className="bg-[#D1FAE5]/30 p-6 rounded-xl">
      <h4 className="font-bold text-[#0F4C3A] mb-2">Pro Tip</h4>
      <p>
        Don't delay seeking treatment. Early intervention often leads to faster recovery 
        and better outcomes. At BC-ICE, we can see you within 24-48 hours of your call.
      </p>
    </div>
  </div>
);

const TMDArticleContent = () => (
  <div className="space-y-6 text-gray-700">
    <h2 className="text-2xl font-bold text-gray-900">What is TMD?</h2>
    <p>
      Temporomandibular Disorders (TMD) affect the jaw joint and surrounding muscles. 
      These conditions can cause significant pain and discomfort, affecting your ability 
      to eat, speak, and even sleep comfortably.
    </p>
    
    <h3 className="text-xl font-bold text-gray-900">Common Symptoms</h3>
    <ul className="list-disc pl-6 space-y-2">
      <li>Pain or tenderness in the jaw, face, or neck</li>
      <li>Clicking, popping, or grinding sounds when opening/closing mouth</li>
      <li>Difficulty chewing or pain while chewing</li>
      <li>Jaw locking in open or closed position</li>
      <li>Headaches, especially upon waking</li>
      <li>Ear pain or ringing in the ears</li>
    </ul>

    <h3 className="text-xl font-bold text-gray-900">Treatment Approaches</h3>
    <p>
      At HeadWorks, our certified specialist Sharon Farquharson uses a comprehensive 
      approach to treating TMD:
    </p>
    <ul className="list-disc pl-6 space-y-2">
      <li>Manual therapy techniques for the jaw and neck</li>
      <li>Postural assessment and correction</li>
      <li>Breathing retraining (Buteyko method)</li>
      <li>Orofacial myofunctional therapy</li>
      <li>Home exercise programs</li>
    </ul>

    <div className="bg-[#C2410C]/10 p-6 rounded-xl">
      <h4 className="font-bold text-[#C2410C] mb-2">Did You Know?</h4>
      <p>
        Sharon Farquharson is one of only 8 Certified Cervical and Temporomandibular 
        Therapists (CCTT) in all of Canada. You're in expert hands at HeadWorks.
      </p>
    </div>
  </div>
);

const PreventionArticleContent = () => (
  <div className="space-y-6 text-gray-700">
    <h2 className="text-2xl font-bold text-gray-900">Prevention is Better Than Cure</h2>
    <p>
      Whether you're sitting at a desk all day or performing physical labor, 
      proper posture and regular movement are essential for preventing workplace injuries.
    </p>
    
    <h3 className="text-xl font-bold text-gray-900">5 Exercises You Can Do at Your Desk</h3>
    
    <div className="space-y-4">
      <div className="bg-gray-50 p-4 rounded-xl">
        <h4 className="font-bold mb-2">1. Chin Tucks</h4>
        <p className="text-sm">
          Sit tall and gently draw your chin back, creating a "double chin." 
          Hold for 5 seconds, repeat 10 times.
        </p>
      </div>
      
      <div className="bg-gray-50 p-4 rounded-xl">
        <h4 className="font-bold mb-2">2. Shoulder Blade Squeezes</h4>
        <p className="text-sm">
          Squeeze your shoulder blades together as if holding a pencil between them. 
          Hold for 5 seconds, repeat 10 times.
        </p>
      </div>
      
      <div className="bg-gray-50 p-4 rounded-xl">
        <h4 className="font-bold mb-2">3. Seated Spinal Twist</h4>
        <p className="text-sm">
          Sit tall, place your right hand on your left knee, and gently rotate 
          your torso to the left. Hold for 15 seconds, switch sides.
        </p>
      </div>
      
      <div className="bg-gray-50 p-4 rounded-xl">
        <h4 className="font-bold mb-2">4. Wrist Circles</h4>
        <p className="text-sm">
          Make circles with your wrists, 10 times in each direction. 
          Great for preventing carpal tunnel syndrome.
        </p>
      </div>
      
      <div className="bg-gray-50 p-4 rounded-xl">
        <h4 className="font-bold mb-2">5. Standing Hip Flexor Stretch</h4>
        <p className="text-sm">
          Stand and step one foot back into a lunge. Push your hips forward 
          until you feel a stretch in the front of your hip. Hold 30 seconds.
        </p>
      </div>
    </div>

    <div className="bg-[#D1FAE5]/30 p-6 rounded-xl">
      <h4 className="font-bold text-[#0F4C3A] mb-2">Expert Recommendation</h4>
      <p>
        Take a 2-minute movement break every 30 minutes of sitting. Set a timer 
        to remind yourself. Your spine will thank you!
      </p>
    </div>
  </div>
);

const GeneralArticleContent = () => (
  <div className="space-y-6 text-gray-700">
    <h2 className="text-2xl font-bold text-gray-900">Your Path to Recovery</h2>
    <p>
      At BC-ICE, we believe in a comprehensive approach to healing that addresses 
      not just symptoms, but the root cause of your condition.
    </p>
    
    <h3 className="text-xl font-bold text-gray-900">Our Treatment Philosophy</h3>
    <p>
      We combine evidence-based treatments with personalized care to help you 
      achieve optimal recovery. Our team works collaboratively to ensure you 
      receive the most effective treatment plan.
    </p>

    <h3 className="text-xl font-bold text-gray-900">What to Expect</h3>
    <ul className="list-disc pl-6 space-y-2">
      <li>Thorough initial assessment</li>
      <li>Personalized treatment plan</li>
      <li>Hands-on therapy sessions</li>
      <li>Home exercise programs</li>
      <li>Regular progress evaluations</li>
      <li>Education on injury prevention</li>
    </ul>

    <div className="bg-[#D1FAE5]/30 p-6 rounded-xl">
      <h4 className="font-bold text-[#0F4C3A] mb-2">Ready to Get Started?</h4>
      <p>
        Book your appointment today and take the first step toward a pain-free life. 
        We offer direct billing for ICBC and WorkSafe BC claims.
      </p>
    </div>
  </div>
);

export default Blog;
